import React, { useState, useCallback } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, Image } from 'react-native';
import { IActivity, QuizActivityConfig, QuizQuestion, KeyStage } from '@eduwrite/shared';
import { useAccessibilityStore } from '../store/useAccessibilityStore';
import { useStageTokens } from '../hooks/useStageTokens';
import { StrokeSyncService } from '../services/StrokeSyncService';
import { AudioPromptButton } from './AudioPromptButton';
import { StageCard } from './primitives/StageCard';

interface QuizActivityProps {
    activity: IActivity;
    onComplete: () => void;
    keyStage?: KeyStage;
}

export const QuizActivity: React.FC<QuizActivityProps> = ({ activity, onComplete, keyStage }) => {
    const config = activity.config as QuizActivityConfig;
    const questions: QuizQuestion[] = config?.questions || [];

    const [currentIndex, setCurrentIndex] = useState(0);
    const [answers, setAnswers] = useState<Record<string, string>>({});
    const [completed, setCompleted] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const highContrast = useAccessibilityStore((s) => s.highContrast);
    const tokens = useStageTokens(keyStage);

    const currentQuestion = questions[currentIndex];
    const isLastQuestion = currentIndex >= questions.length - 1;
    const hasSelectedAnswer = currentQuestion ? !!answers[currentQuestion.id] : false;

    const handleSelectOption = useCallback((optionId: string) => {
        if (!currentQuestion) return;
        setAnswers(prev => ({ ...prev, [currentQuestion.id]: optionId }));
        // Clear any previous error when the child makes a new selection
        setErrorMessage(null);
    }, [currentQuestion]);

    const handleNext = useCallback(async () => {
        if (!currentQuestion || !hasSelectedAnswer) return;

        const selectedOptionId = answers[currentQuestion.id];
        const correctCount = questions.filter(q =>
            answers[q.id] === q.correctOptionId
        ).length;

        try {
            await StrokeSyncService.sendQuizAnswer({
                activityId: activity.id,
                questionId: currentQuestion.id,
                selectedOptionId,
                questionIndex: currentIndex,
                totalQuestions: questions.length,
                score: correctCount,
            });
        } catch {
            // Gentle error for children — never harsh, never silent.
            // Store the message in state and render it near the action.
            setErrorMessage("Let's try again!");
            return;
        }

        if (currentIndex < questions.length - 1) {
            setCurrentIndex(prev => prev + 1);
            setErrorMessage(null);
        } else {
            setCompleted(true);
        }
    }, [currentQuestion, hasSelectedAnswer, answers, currentIndex, questions.length, activity.id]);

    const handleBack = useCallback(() => {
        if (currentIndex > 0) {
            setCurrentIndex(prev => prev - 1);
            setErrorMessage(null);
        }
    }, [currentIndex]);

    const handleFinish = useCallback(() => {
        onComplete();
    }, [onComplete]);

    if (completed) {
        const correctCount = questions.filter(q => answers[q.id] === q.correctOptionId).length;
        return (
            <ScrollView
                style={styles.container}
                contentContainerStyle={styles.scrollWrapper}
                showsVerticalScrollIndicator={true}
                testID="quiz-activity"
            >
                <StageCard style={styles.completionContainer}>
                    <Text style={styles.completionTitle}>Great job!</Text>
                    <Text style={styles.completionScore}>
                        You got {correctCount} out of {questions.length} right!
                    </Text>
                    <TouchableOpacity
                        style={[styles.finishButton, { minHeight: tokens.touchTargetMin }]}
                        onPress={handleFinish}
                        activeOpacity={0.7}
                    >
                        <Text style={styles.finishButtonText}>Finish</Text>
                    </TouchableOpacity>
                </StageCard>
            </ScrollView>
        );
    }

    if (!currentQuestion) {
        return (
            <ScrollView
                style={styles.container}
                contentContainerStyle={styles.scrollWrapper}
                showsVerticalScrollIndicator={true}
                testID="quiz-activity"
            >
                <StageCard style={styles.completionContainer}>
                    <Text style={styles.emptyText}>No quiz questions available.</Text>
                    <TouchableOpacity
                        style={[styles.finishButton, { minHeight: tokens.touchTargetMin }]}
                        onPress={onComplete}
                        activeOpacity={0.7}
                    >
                        <Text style={styles.finishButtonText}>Finish</Text>
                    </TouchableOpacity>
                </StageCard>
            </ScrollView>
        );
    }

    const progressText = `${currentIndex + 1} of ${questions.length}`;
    const progressPercent = ((currentIndex) / questions.length) * 100;

    const optionColors = highContrast
        ? { bg: '#FFFFFF', border: '#000000', text: '#000000', selectedBg: '#000000', selectedText: '#FFFFFF', selectedBorder: '#000000' }
        : { bg: '#F3F4F6', border: '#E5E7EB', text: '#1F2937', selectedBg: '#3B82F6', selectedText: '#FFFFFF', selectedBorder: '#3B82F6' };

    // Show AudioPromptButton for EYFS/KS1 (pre-readers and early readers)
    const showAudioPrompt = tokens.isEYFS || (!tokens.isKS2);

    return (
        <ScrollView
            style={styles.container}
            contentContainerStyle={styles.scrollWrapper}
            showsVerticalScrollIndicator={true}
            testID="quiz-activity"
        >
            <StageCard style={styles.cardContainer}>
                <View style={styles.header}>
                <View style={styles.headerRow}>
                    {currentIndex > 0 && (
                        <TouchableOpacity
                            style={[styles.backButton, { minHeight: tokens.touchTargetSecondary, minWidth: tokens.touchTargetSecondary }]}
                            onPress={handleBack}
                            activeOpacity={0.7}
                            testID="quiz-back-button"
                        >
                            <Text style={styles.backButtonText}>Back</Text>
                        </TouchableOpacity>
                    )}
                    <Text style={styles.progressText}>{progressText}</Text>
                </View>
                <View style={styles.progressBar}>
                    <View
                        style={[
                            styles.progressFill,
                            { width: `${progressPercent}%` },
                        ]}
                    />
                </View>
            </View>

            <View style={styles.questionContainer}>
                <Text style={[styles.questionText, { fontSize: tokens.titleFontSize }]}>
                    {currentQuestion.text}
                </Text>
                {showAudioPrompt && (
                    <AudioPromptButton
                        text={currentQuestion.audioPromptText || currentQuestion.text}
                        audioUrl={currentQuestion.audioPromptUrl}
                        autoPlay={tokens.autoPlayAudio}
                        testID="audio-prompt"
                    />
                )}
            </View>

            <View style={styles.optionsContainer}>
                {currentQuestion.options.map((option) => {
                    const isSelected = answers[currentQuestion.id] === option.id;
                    return (
                        <TouchableOpacity
                            key={option.id}
                            testID={`quiz-option-${option.id}`}
                            style={[
                                styles.optionButton,
                                {
                                    backgroundColor: isSelected ? optionColors.selectedBg : optionColors.bg,
                                    borderColor: isSelected ? optionColors.selectedBorder : optionColors.border,
                                    minHeight: tokens.touchTargetMin,
                                },
                            ]}
                            onPress={() => handleSelectOption(option.id)}
                            activeOpacity={0.7}
                        >
                            {option.imageUrl ? (
                                <View style={styles.optionImageContainer}>
                                    <Image
                                        source={{ uri: option.imageUrl }}
                                        style={[styles.optionImage, { height: tokens.imageHeight }]}
                                        resizeMode="contain"
                                        testID="option-image"
                                    />
                                    <Text style={[styles.optionText, { color: isSelected ? optionColors.selectedText : optionColors.text, fontSize: tokens.bodyFontSize }]}>
                                        {option.text}
                                    </Text>
                                </View>
                            ) : (
                                <Text style={[styles.optionText, { color: isSelected ? optionColors.selectedText : optionColors.text, fontSize: tokens.bodyFontSize }]}>
                                    {option.text}
                                </Text>
                            )}
                        </TouchableOpacity>
                    );
                })}
            </View>

            {errorMessage && (
                <View style={styles.errorContainer}>
                    <Text style={styles.errorText}>{errorMessage}</Text>
                </View>
            )}

            <View style={styles.actionRow}>
                {isLastQuestion ? (
                    <TouchableOpacity
                        style={[
                            styles.nextButton,
                            { minHeight: tokens.touchTargetMin, opacity: hasSelectedAnswer ? 1 : 0.5 },
                        ]}
                        onPress={handleNext}
                        disabled={!hasSelectedAnswer}
                        activeOpacity={0.7}
                    >
                        <Text style={styles.nextButtonText}>Finish</Text>
                    </TouchableOpacity>
                ) : (
                    <TouchableOpacity
                        style={[
                            styles.nextButton,
                            { minHeight: tokens.touchTargetMin, opacity: hasSelectedAnswer ? 1 : 0.5 },
                        ]}
                        onPress={handleNext}
                        disabled={!hasSelectedAnswer}
                        activeOpacity={0.7}
                    >
                        <Text style={styles.nextButtonText}>Next</Text>
                    </TouchableOpacity>
                )}
            </View>
            </StageCard>
        </ScrollView>
    );
};

const styles = StyleSheet.create({
    scrollWrapper: {
        flexGrow: 1,
        padding: 16,
        justifyContent: 'center',
    },
    cardContainer: {
        borderRadius: 28,
        padding: 20,
        backgroundColor: '#FFFFFF',
    },
    container: {
        flex: 1,
        width: '100%',
        minHeight: 0,
    },
    completionContainer: {
        borderRadius: 28,
        padding: 28,
        backgroundColor: '#FFFFFF',
        alignItems: 'center',
        justifyContent: 'center',
    },
    completionTitle: {
        fontSize: 32,
        fontWeight: 'bold',
        color: '#10B981',
        marginBottom: 12,
    },
    completionScore: {
        fontSize: 20,
        color: '#6B7280',
        marginBottom: 32,
    },
    header: {
        marginBottom: 20,
    },
    headerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        marginBottom: 8,
    },
    backButton: {
        backgroundColor: '#F3F4F6',
        borderRadius: 12,
        borderWidth: 2,
        borderColor: '#E5E7EB',
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 16,
    },
    backButtonText: {
        fontSize: 16,
        fontWeight: '600',
        color: '#1F2937',
    },
    progressText: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#6B7280',
    },
    progressBar: {
        height: 8,
        backgroundColor: '#E5E7EB',
        borderRadius: 4,
        overflow: 'hidden',
    },
    progressFill: {
        height: '100%',
        backgroundColor: '#3B82F6',
        borderRadius: 4,
    },
    questionContainer: {
        marginBottom: 24,
        flexDirection: 'column',
        gap: 8,
    },
    questionText: {
        fontWeight: 'bold',
        color: '#1F2937',
        lineHeight: 30,
    },
    optionsContainer: {
        gap: 12,
        marginBottom: 24,
    },
    optionButton: {
        paddingVertical: 16,
        paddingHorizontal: 20,
        borderRadius: 12,
        borderWidth: 2,
        justifyContent: 'center',
    },
    optionImageContainer: {
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
    },
    optionImage: {
        width: '100%',
        borderRadius: 8,
    },
    optionText: {
        fontWeight: '600',
        textAlign: 'center',
    },
    errorContainer: {
        backgroundColor: '#FEF3C7',
        borderRadius: 12,
        padding: 12,
        marginBottom: 16,
        borderWidth: 1,
        borderColor: '#FCD34D',
    },
    errorText: {
        fontSize: 18,
        fontWeight: '600',
        color: '#92400E',
        textAlign: 'center',
    },
    actionRow: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: 12,
    },
    nextButton: {
        backgroundColor: '#3B82F6',
        borderRadius: 12,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 40,
    },
    nextButtonText: {
        color: '#FFFFFF',
        fontSize: 18,
        fontWeight: 'bold',
    },
    finishButton: {
        backgroundColor: '#3B82F6',
        paddingVertical: 16,
        borderRadius: 12,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 40,
    },
    finishButtonText: {
        color: '#FFFFFF',
        fontSize: 18,
        fontWeight: 'bold',
    },
    emptyText: {
        fontSize: 18,
        color: '#6B7280',
        textAlign: 'center',
        marginBottom: 16,
    },
});
